import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { blogIndexQuery } from "@/lib/blog/queries";
import { PostCard } from "@/components/blog/PostCard";
import { Pagination } from "@/components/blog/Pagination";
import { BlogBanner } from "@/components/blog/BlogBanner";
import { PAGE_SIZE, normalizarPagina, paginar, totalPaginas } from "@/lib/blog/leitura";

const BASE = "https://fisioemortopedia.com.br";
const TITLE = "E-FISIO · Artigos de fisioterapia em ortopedia";
const DESCRIPTION =
  "Artigos de fisioterapia musculoesquelética baseados em evidência: avaliação, dor, esporte, traumatologia, MMII e MMSS. Conteúdo do E-FISIO, portal da Fisio em Ortopedia.";

export const Route = createFileRoute("/e-fisio/")({
  validateSearch: (search: Record<string, unknown>) => {
    const n = Number(search.page);
    const q = typeof search.q === "string" && search.q.trim() ? search.q.trim() : undefined;
    return { page: Number.isFinite(n) && n > 1 ? Math.trunc(n) : undefined, q } as {
      page?: number;
      q?: string;
    };
  },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ context, deps }) => {
    const data = await context.queryClient.ensureQueryData(blogIndexQuery);
    return { page: normalizarPagina(deps.page ?? 1, data.posts.length) };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page ?? 1;
    const url = page > 1 ? `${BASE}/e-fisio?page=${page}` : `${BASE}/e-fisio`;
    const title = page > 1 ? `${TITLE} · página ${page}` : TITLE;
    return {
      meta: [
        { title },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: title },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", type: "application/rss+xml", title: "E-FISIO", href: `${BASE}/e-fisio/rss.xml` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "E-FISIO",
            description: DESCRIPTION,
            url: `${BASE}/e-fisio`,
            inLanguage: "pt-BR",
            publisher: {
              "@type": "Organization",
              name: "Fisio em Ortopedia",
              url: BASE,
            },
          }),
        },
      ],
    };
  },
  errorComponent: BlogIndexError,
  component: BlogIndex,
});

function BlogIndexError() {
  return (
    <main className="container mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-[var(--efisio-navy)]">
        Não conseguimos carregar os artigos agora
      </h1>
      <p className="mt-3 text-[var(--efisio-muted)]">Tente recarregar a página em alguns instantes.</p>
    </main>
  );
}

function BlogIndex() {
  const { data } = useSuspenseQuery(blogIndexQuery);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [busca, setBusca] = useState(search.q ?? "");
  const [categoria, setCategoria] = useState<string | null>(null);

  const filtrando = busca.trim().length > 0 || categoria !== null;

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return data.posts.filter((p) => {
      const okCat = !categoria || p.categoria_slugs?.includes(categoria);
      const okBusca =
        !termo ||
        p.titulo.toLowerCase().includes(termo) ||
        (p.subtitulo ?? "").toLowerCase().includes(termo);
      return okCat && okBusca;
    });
  }, [data.posts, busca, categoria]);

  const page = normalizarPagina(filtrando ? 1 : (search.page ?? 1), filtrados.length);
  const totalPages = totalPaginas(filtrados.length);
  const visiveis = paginar(filtrados, page);

  const resetarPagina = () => {
    if (search.page) void navigate({ search: {}, resetScroll: false });
  };

  return (
    <main>
      <BlogBanner
        logoComoTitulo
        busca={busca}
        onBusca={(v) => {
          setBusca(v);
          resetarPagina();
        }}
      />

      <section className="container mx-auto px-4 py-10 sm:py-14">
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setCategoria(null);
              resetarPagina();
            }}
            aria-pressed={categoria === null}
            className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
              categoria === null
                ? "border-transparent bg-[var(--efisio-navy)] text-white"
                : "border-[var(--efisio-border)] bg-[var(--efisio-surface)] text-[var(--efisio-navy)]"
            }`}
          >
            Todos
          </button>
          {data.categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => {
                setCategoria((cur) => (cur === c.slug ? null : c.slug));
                resetarPagina();
              }}
              aria-pressed={categoria === c.slug}
              className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                categoria === c.slug
                  ? "border-transparent bg-[var(--efisio-navy)] text-white"
                  : "border-[var(--efisio-border)] bg-[var(--efisio-surface)] text-[var(--efisio-navy)]"
              }`}
            >
              {c.nome}
              <span className="ml-1.5 opacity-60">
                {data.posts.filter((p) => p.categoria_slugs?.includes(c.slug)).length}
              </span>
            </button>
          ))}
        </div>

        <h2 className="sr-only">Artigos</h2>

        {visiveis.length === 0 ? (
          <p className="rounded-2xl border border-[var(--efisio-border)] bg-[var(--efisio-surface)] p-8 text-center text-[var(--efisio-muted)]">
            Nenhum artigo encontrado.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visiveis.map((post, i) => (
              <PostCard
                key={post.slug}
                post={post}
                categories={data.categories}
                priority={i === 0 && (filtrando || page > 1)}
              />
            ))}
          </div>
        )}

        {!filtrando && (
          <Pagination
            page={page}
            totalPages={totalPages}
            renderLink={(p, children, className) => (
              <Link
                key={String(children)}
                to="/e-fisio"
                search={p > 1 ? { page: p } : {}}
                className={className}
              >
                {children}
              </Link>
            )}
          />
        )}

        {filtrando && filtrados.length > PAGE_SIZE && (
          <p className="mt-8 text-center text-sm text-[var(--efisio-muted)]">
            Mostrando os {PAGE_SIZE} primeiros de {filtrados.length} resultados. Refine a busca para
            ver outros artigos.
          </p>
        )}

      </section>
    </main>
  );
}
